import React, {useEffect, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useStores} from "../../../stores/useStores";
import {AppAccessStatus} from "../../../shared/enums/appAccessStatus";
import ResidentMetricCard from "../../molecules/residentMetricCard/residentMetricCard";
import {VillageMetric} from "../../../stores/domainStore/pages/models/villageMetric";
import {VillageMetricBuilder} from "../../../utils/villageMetricBuilder";
import {IResidentAdminDomainStore} from "../../../stores/domainStore/pages/residentAdminDomainStore";
import {IResidentAdminUiStore} from "../../../stores/uiStore/pages/residentAdminUiStore";

export interface IResidentAdminVillageMetricsProps {
  // Overrides let the serviced-apartment page reuse these metrics; default to the independent stores.
  residentAdminDomainStore?: IResidentAdminDomainStore;
  residentAdminUiStore?: IResidentAdminUiStore;
}

const ResidentAdminVillageMetrics: React.FC<
  IResidentAdminVillageMetricsProps
> = ({
  residentAdminDomainStore: injectedDomainStore,
  residentAdminUiStore: injectedUiStore
}) => {
  const {
    domainStore: {independentResidentAdminDomainStore: defaultDomainStore},
    uiStore: {independentResidentAdminUiStore: defaultUiStore}
  } = useStores();

  const residentAdminDomainStore = injectedDomainStore ?? defaultDomainStore;
  const residentAdminUiStore = injectedUiStore ?? defaultUiStore;

  const [selectedAppAccessStatus, setAppAccessStatus] = React.useState<
    AppAccessStatus | undefined
  >(undefined);
  const {t} = useTranslation(["residentadmin"]);

  // Populate reactively using data from the residentAdminDomainStore
  const villageMetrics: VillageMetric[] = useMemo(
    () =>
      VillageMetricBuilder.build(
        residentAdminDomainStore.residents, // residents: ResidentDetail[],
        t("residentadmin:labels.metricActiveAccountsTitle"), // activeAccountsTitle
        t("residentadmin:labels.metricActiveAccounts"), // activeAccountsDescription
        t("residentadmin:labels.metricHouseholdAccountsTitle"), // accessAccountsTitle
        t("residentadmin:labels.metricHouseholdAccounts"), // accessAccountsDescription
        t("residentadmin:labels.metricInactiveAccountsTitle"), // inactiveAccountsTitle
        t("residentadmin:labels.metricInactiveAccounts"), // inactiveAccountsDescription
        t("residentadmin:labels.metricRequiresActionAccountsTitle"), // requiresActionTitle
        t("residentadmin:labels.metricRequiresActionAccounts") // requiresActionDescription
      ),
    [residentAdminDomainStore.residents, t]
  );

  const onMetricCardClick = (appAccessStatus: AppAccessStatus) => {
    // Set the new appAccessStatus or toggle off the previous
    let newAppAccessStatus =
      appAccessStatus === selectedAppAccessStatus ? undefined : appAccessStatus;

    setAppAccessStatus(newAppAccessStatus);

    residentAdminUiStore.setStatusFilter(newAppAccessStatus);
  };

  useEffect(() => {
    if (residentAdminUiStore.statusFilter === undefined) {
      setAppAccessStatus(undefined);
    }
  }, [residentAdminUiStore.statusFilter]);

  return (
    <div className="village-metrics" data-testid="villageMetrics">
      {villageMetrics.map(
        ({
          title,
          description,
          count,
          valueDisplay,
          percentage,
          tagType,
          appAccessStatus
        }) => (
          <div className="metric-margin" key={title}>
            <ResidentMetricCard
              title={title}
              description={description}
              metricValue={valueDisplay}
              metricPercent={percentage}
              tagType={tagType}
              isLoading={residentAdminUiStore.isListLoading}
              isSelected={selectedAppAccessStatus === appAccessStatus}
              enabled={count > 0}
              onClick={() => onMetricCardClick(appAccessStatus)}
            />
          </div>
        )
      )}
    </div>
  );
};

export default ResidentAdminVillageMetrics;
